import {
  Button,
  MenuItem,
  NumericInput,
  Radio,
  RadioGroup,
} from "@blueprintjs/core";
import { DateInput } from "@blueprintjs/datetime";
import { Select } from "@blueprintjs/select";
import type { ScheduledEventRecurringSettings } from "@package/ui-gql/index-data-access";
import moment from "moment";
import { useMemo } from "react";
import { Frequency } from "rrule";
import {
  MonthEnum,
  MonthEnumNames,
  WeekdayEnum,
  WeekdayEnumNames,
} from "../../../../../constants/contants";
import { DashboardCard } from "../../../../DashboardCard/DashboardCard";
import { summarizeRecurrence } from "../scheduler-recurrence-utils";
import { SchedulerOccurrencePreview } from "./SchedulerOccurrencePreview";
import { SchedulerTogglePills } from "./SchedulerTogglePills";

// Sunday-first display order to match FullCalendar's default `firstDay`
// grid (SchedulerCalendar doesn't override it) -- WeekdayEnum's own values
// stay Monday=0..Sunday=6 (the ISO/rrule convention the backend expects),
// this only reorders how the pills are laid out.
const WEEKDAY_DISPLAY_ORDER: WeekdayEnum[] = [
  WeekdayEnum.Sunday,
  WeekdayEnum.Monday,
  WeekdayEnum.Tuesday,
  WeekdayEnum.Wednesday,
  WeekdayEnum.Thursday,
  WeekdayEnum.Friday,
  WeekdayEnum.Saturday,
];

const WEEKDAY_OPTIONS = WEEKDAY_DISPLAY_ORDER.map((value) => ({
  value,
  label: WeekdayEnumNames[value].slice(0, 1),
  title: WeekdayEnumNames[value],
}));

const MONTH_OPTIONS = Object.values(MonthEnum)
  .filter((value): value is MonthEnum => typeof value === "number")
  .map((value) => ({
    value,
    label: MonthEnumNames[value].slice(0, 3),
    title: MonthEnumNames[value],
  }));

// Explicit day → week → month → year order, matching the Google Calendar
// picker this builder imitates -- Object.entries() over the enum's integer
// keys would otherwise iterate in ascending numeric order (year first).
const FREQUENCY_OPTIONS: Frequency[] = [
  Frequency.DAILY,
  Frequency.WEEKLY,
  Frequency.MONTHLY,
  Frequency.YEARLY,
];

const FREQUENCY_UNIT_LABEL: Record<number, string> = {
  [Frequency.DAILY]: "day",
  [Frequency.WEEKLY]: "week",
  [Frequency.MONTHLY]: "month",
  [Frequency.YEARLY]: "year",
};

// Mirrors MAX_RECURRING_OCCURRENCES in scheduled-event.service.ts -- "Never"
// is materialized as this many documents, not an unbounded series, so the
// picker should say so rather than imply an open-ended schedule.
const MAX_RECURRING_OCCURRENCES = 1000;

type EndsMode = "never" | "on" | "after";

function getEndsMode(recurring: ScheduledEventRecurringSettings): EndsMode {
  if (recurring.until) {
    return "on";
  }
  if (recurring.count) {
    return "after";
  }
  return "never";
}

type SchedulerRecurrenceEditorProps = {
  disabled?: boolean;
  recurring: ScheduledEventRecurringSettings | null | undefined;
  start?: Date | string | null;
  timezone?: string | null;
  onChange: (recurring: ScheduledEventRecurringSettings | null) => void;
};

export function SchedulerRecurrenceEditor({
  disabled,
  onChange,
  recurring,
  start,
  timezone,
}: SchedulerRecurrenceEditorProps) {
  const enabled = !!recurring;
  const fallbackStart = useMemo(
    () => (start ? new Date(start) : new Date()),
    [start],
  );

  const updateRecurring = (patch: Partial<ScheduledEventRecurringSettings>) => {
    onChange({
      freq: Frequency.WEEKLY,
      interval: 1,
      byweekday: undefined,
      bymonth: undefined,
      count: undefined,
      until: undefined,
      ...recurring,
      // Re-derived from the current props on every update, not just seeded
      // once from `...recurring` -- otherwise changing the event's Start or
      // Timezone after enabling Repeat leaves the rule pinned to whatever
      // they were the moment Repeat was turned on (both here, for the live
      // preview below, and in the payload that reaches the server).
      dtstart: fallbackStart.toISOString(),
      tzid: timezone ?? undefined,
      ...patch,
    });
  };

  const freq = recurring?.freq ?? Frequency.WEEKLY;
  const interval = recurring?.interval ?? 1;
  const endsMode = recurring ? getEndsMode(recurring) : "never";

  return (
    <DashboardCard title="Repeat">
      <RadioGroup
        disabled={disabled}
        inline
        onChange={(event) => {
          if (event.currentTarget.value === "custom") {
            updateRecurring({});
          } else {
            onChange(null);
          }
        }}
        selectedValue={enabled ? "custom" : "none"}
      >
        <Radio label="Does not repeat" value="none" />
        <Radio label="Custom" value="custom" />
      </RadioGroup>

      {enabled && recurring && (
        <div className="mt-3 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <span className="shrink-0 text-sm text-brand-ink">
              Repeat every
            </span>
            {/*
              No width class here -- Blueprint's .bp6-control-group sets
              flex-shrink:0 on its own children, so this NumericInput's
              rendered content (the browser's unstyled default <input> width,
              ~170px+) can't actually shrink to fit a w-20/w-24/etc box.
              Constraining the wrapper just made the real content overflow
              past it and visually overlap the Select next to it. Letting it
              size to its own natural width is what actually fixes that.
            */}
            <NumericInput
              clampValueOnBlur
              disabled={disabled}
              min={1}
              max={500}
              onValueChange={(value) =>
                updateRecurring({ interval: Math.max(1, value || 1) })
              }
              value={interval}
            />
            <Select<Frequency>
              disabled={disabled}
              filterable={false}
              items={FREQUENCY_OPTIONS}
              itemRenderer={(
                frequency,
                { handleClick, handleFocus, modifiers },
              ) => (
                <MenuItem
                  active={modifiers.active}
                  disabled={modifiers.disabled}
                  key={frequency}
                  onClick={handleClick}
                  onFocus={handleFocus}
                  roleStructure="listoption"
                  text={
                    interval > 1
                      ? `${FREQUENCY_UNIT_LABEL[frequency]}s`
                      : FREQUENCY_UNIT_LABEL[frequency]
                  }
                />
              )}
              onItemSelect={(nextFreq) => {
                // Clear byweekday/bymonth on a frequency change rather than
                // carrying them over silently -- a stale byweekday left set
                // while freq is monthly/yearly changes rrule's meaning
                // (Nth-weekday-of-month) instead of just being ignored.
                updateRecurring({
                  freq: nextFreq,
                  byweekday:
                    nextFreq === Frequency.WEEKLY
                      ? recurring.byweekday
                      : undefined,
                  bymonth:
                    nextFreq === Frequency.MONTHLY ||
                    nextFreq === Frequency.YEARLY
                      ? recurring.bymonth
                      : undefined,
                });
              }}
              popoverProps={{ minimal: true }}
            >
              {/*
                No `fill` here -- Select's own `fill` prop only takes effect
                when its target is also `fill`, and a full-width button in
                this compact "1 week(s)" row blew up to the row's entire
                available width, overlapping the NumericInput beside it. A
                fixed width is the right fit for a two-word "month(s)" label.
              */}
              <Button
                alignText="left"
                className="w-28"
                disabled={disabled}
                minimal
                outlined
                rightIcon="caret-down"
                text={
                  interval > 1
                    ? `${FREQUENCY_UNIT_LABEL[freq]}s`
                    : FREQUENCY_UNIT_LABEL[freq]
                }
              />
            </Select>
          </div>

          {freq === Frequency.WEEKLY && (
            <div>
              <p className="mb-1.5 text-sm text-brand-ink">Repeat on</p>
              <SchedulerTogglePills
                disabled={disabled}
                onChange={(byweekday) => updateRecurring({ byweekday })}
                options={WEEKDAY_OPTIONS}
                selected={recurring.byweekday ?? []}
              />
            </div>
          )}

          {(freq === Frequency.MONTHLY || freq === Frequency.YEARLY) && (
            <div>
              <p className="mb-1.5 text-sm text-brand-ink">Repeat in</p>
              <SchedulerTogglePills
                disabled={disabled}
                onChange={(bymonth) => updateRecurring({ bymonth })}
                options={MONTH_OPTIONS}
                selected={recurring.bymonth ?? []}
              />
            </div>
          )}

          <div>
            <p className="mb-1.5 text-sm text-brand-ink">Ends</p>
            <RadioGroup
              disabled={disabled}
              onChange={(event) => {
                const value = event.currentTarget.value as EndsMode;

                if (value === "never") {
                  updateRecurring({ until: undefined, count: undefined });
                } else if (value === "on") {
                  updateRecurring({
                    until: moment(fallbackStart).add(1, "month").toISOString(),
                    count: undefined,
                  });
                } else {
                  updateRecurring({ count: 12, until: undefined });
                }
              }}
              selectedValue={endsMode}
            >
              <Radio label="Never" value="never" />
              {endsMode === "never" && (
                <p className="ml-6 -mt-1 mb-1 text-xs text-brand-muted">
                  Capped at {MAX_RECURRING_OCCURRENCES.toLocaleString()}{" "}
                  occurrences.
                </p>
              )}
              {/*
                RadioGroup only wires up checked/name/onChange on Radio
                elements that are its own direct children (it does a shallow
                Children.map, not a deep tree walk) -- wrapping "On"/"After"
                in a sibling <div> alongside a bare <Radio> left both radios
                un-grouped, so each behaved as its own independent one-radio
                group instead of being mutually exclusive with "Never". The
                companion widgets go in as `children`, which Radio renders
                as part of its own <label> right after the radio indicator.
              */}
              <Radio value="on">
                <span className="inline-flex items-center gap-2 align-middle">
                  <span>On</span>
                  <DateInput
                    closeOnSelection
                    dateFnsFormat="LLL do, yyyy"
                    disabled={disabled || endsMode !== "on"}
                    onChange={(date) =>
                      updateRecurring({
                        until: date ? new Date(date).toISOString() : undefined,
                      })
                    }
                    popoverProps={{ minimal: true }}
                    timezone={timezone ?? undefined}
                    value={recurring.until ?? null}
                  />
                </span>
              </Radio>
              <Radio value="after">
                <span className="inline-flex items-center gap-2 align-middle">
                  <span>After</span>
                  <NumericInput
                    clampValueOnBlur
                    disabled={disabled || endsMode !== "after"}
                    min={1}
                    max={1000}
                    onValueChange={(value) =>
                      updateRecurring({ count: Math.max(1, value || 1) })
                    }
                    value={recurring.count ?? 1}
                  />
                  <span className="text-sm text-brand-muted">occurrences</span>
                </span>
              </Radio>
            </RadioGroup>
          </div>

          <div className="border-t border-brand-line pt-3 text-xs text-brand-muted">
            {summarizeRecurrence(recurring, fallbackStart)}
          </div>
          <SchedulerOccurrencePreview
            recurring={recurring}
            start={fallbackStart}
          />
        </div>
      )}
    </DashboardCard>
  );
}
